// Fichier: pages/PolitiqueConfidentialite.js
import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ThemeProvider as CustomThemeProvider, useTheme } from '../context/ThemeContext';

// Style global unique - exactement comme dans HomePage.js
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Poppins', sans-serif;
    transition: background-color 0.3s ease, 
                color 0.3s ease, 
                border-color 0.3s ease, 
                box-shadow 0.3s ease;
    -webkit-tap-highlight-color: transparent; /* Supprime le highlight bleu sur mobile */
    -webkit-touch-callout: none; /* Désactive le menu contextuel sur iOS */
    -webkit-user-select: none; /* Empêche la sélection de texte sur WebKit */
    user-select: none; /* Standard syntax */
  }
  
  html, body {
    overflow-x: hidden;
    scroll-behavior: smooth;
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }
  
  a {
    text-decoration: none;
    color: inherit;
  }
  
  button {
    cursor: pointer;
    border: none;
    outline: none;
  }

  /* Permet la sélection de texte pour les paragraphes et autres contenus textuels */
  p, span, div, label {
    user-select: text;
  }
`;

const ConfidentialiteSection = styled.section`
  padding: 10rem 5% 4rem;
  background-color: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.text};
  
  span {
    color: ${props => props.theme.colors.accent};
  }
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.accentGradient};
    margin-top: 1rem;
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text};
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const Paragraph = styled.p`
  line-height: 1.7;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.secondaryText};
`;

const List = styled.ul`
  margin-left: 1.5rem;
  margin-bottom: 1.5rem;
`;

const ListItem = styled.li`
  line-height: 1.7;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.secondaryText};
`;

const Link = styled.a`
  color: ${props => props.theme.colors.accent};
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: 2px;
  }
`;

// Wrapper avec le thème, similaire à HomePageWrapper dans HomePage.js
const ConfidentialiteWrapper = () => {
    const { theme } = useTheme();
    const [isScrolled, setIsScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />
                <Helmet>
                    <title>Politique de confidentialité - Rankeed | Création de sites web</title>
                    <meta name="description" content="Politique de confidentialité et protection des données personnelles de Rankeed, agence de création de sites web professionnels." />
                    <link rel="canonical" href="https://rankeed.fr/politique-confidentialite" />
                </Helmet>

                <Header
                    isScrolled={isScrolled}
                    menuOpen={menuOpen}
                    setMenuOpen={setMenuOpen}
                    isStandalonePage={true}
                />

                <ConfidentialiteContent />

                <Footer />
            </>
        </ThemeProvider>
    );
};

// Contenu principal de la page
const ConfidentialiteContent = () => {
    return (
        <ConfidentialiteSection>
            <Container>
                <PageTitle>Politique de <span>confidentialité</span></PageTitle>

                <Section>
                    <SectionTitle>1. Données collectées</SectionTitle>
                    <Paragraph>
                        Lors de votre navigation ou lors de la prise de contact, les données suivantes peuvent être collectées :
                    </Paragraph>
                    <List>
                        <ListItem>Prénom, nom (si fournis)</ListItem>
                        <ListItem>Adresse email</ListItem>
                        <ListItem>URL de votre site (si fournie)</ListItem>
                        <ListItem>Informations sur votre projet de site web</ListItem>
                    </List>
                </Section>

                <Section>
                    <SectionTitle>2. Finalité des données</SectionTitle>
                    <Paragraph>
                        Les données sont collectées afin de :
                    </Paragraph>
                    <List>
                        <ListItem>Répondre à vos demandes de contact</ListItem>
                        <ListItem>Gérer les projets de création de sites</ListItem>
                        <ListItem>Vous envoyer des informations ou devis personnalisés</ListItem>
                    </List>
                </Section>

                <Section>
                    <SectionTitle>3. Durée de conservation</SectionTitle>
                    <Paragraph>
                        Les données sont conservées jusqu'à 24 mois après la dernière interaction ou demande de suppression.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>4. Partage des données</SectionTitle>
                    <Paragraph>
                        Les données ne sont jamais revendues. Elles peuvent être partagées avec des prestataires techniques (hébergeur, outils de gestion de contact), uniquement si cela est nécessaire au fonctionnement du service.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>5. Sécurité</SectionTitle>
                    <Paragraph>
                        Vos données sont hébergées chez Hostinger et font l'objet d'une sécurisation via HTTPS et outils internes.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>6. Droits des utilisateurs</SectionTitle>
                    <Paragraph>
                        Conformément au RGPD, vous disposez des droits suivants :
                    </Paragraph>
                    <List>
                        <ListItem>Accès à vos données</ListItem>
                        <ListItem>Rectification</ListItem>
                        <ListItem>Suppression</ListItem>
                        <ListItem>Opposition au traitement</ListItem>
                        <ListItem>Portabilité</ListItem>
                    </List>
                    <Paragraph>
                        Pour exercer vos droits : <Link href="mailto:contact.sumfy@gmail.com">contact.sumfy@gmail.com</Link>
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>7. Cookies</SectionTitle>
                    <Paragraph>
                        Rankeed utilise uniquement des cookies techniques nécessaires au bon fonctionnement du site. Aucun cookie publicitaire n'est utilisé.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>8. Modifications de la politique de confidentialité</SectionTitle>
                    <Paragraph>
                        La présente politique de confidentialité peut être mise à jour. Les utilisateurs seront informés des changements significatifs par une notification sur le site.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>9. Contact</SectionTitle>
                    <Paragraph>
                        Pour toute question concernant notre politique de confidentialité, veuillez nous contacter à l'adresse suivante : <Link href="mailto:contact.sumfy@gmail.com">contact.sumfy@gmail.com</Link>
                    </Paragraph>
                </Section>
            </Container>
        </ConfidentialiteSection>
    );
};

// Composant avec le context ThemeProvider
const PolitiqueConfidentialitePage = () => {
    return (
        <CustomThemeProvider>
            <ConfidentialiteWrapper />
        </CustomThemeProvider>
    );
};

export default PolitiqueConfidentialitePage;